import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { AuthService } from '@core/auth.service';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  GameOnboardingPopupDoc,
  OnboardingPopupCloseReason,
  OnboardingPopupDialogData,
} from 'src/app/interfaces/game-onboarding-popup.interface';
import { SafeHtmlPipe } from 'src/app/pipe/safehtmlpipe';
import {
  ONBOARDING_SPRITE,
  fallbackCurrencyBeautifyValue,
  pickPopupContent,
  renderPopupLine,
  resolveBannerIcon,
  resolveHeaderIcon,
  resolveRowIcons,
  stripCurrencyBeautifyTokens,
  withPopupInlineImages,
} from './onboarding-popup.contract';

interface OnboardingPopupView {
  background: SafeStyle;
  textColor: string;
  headerIconBackground: string;
  headerIcon: string;
  title: string;
  bannerBackground: SafeStyle;
  bannerTextColor: string;
  bannerIcon: string;
  prizeLabel: string;
  panelColor: string;
  rowIcons: [string | undefined, string | undefined, string | undefined];
  ctaText: string;
  ctaBackground: string;
  ctaTextColor: string;
  ctaShadowColor: string;
  showCta: boolean;
  patternCss: string;
  layout: 'default' | 'prize_box_opened' | 'challenge_intro';
}

@Component({
  selector: 'app-game-onboarding-popup',
  standalone: true,
  imports: [CommonModule, SafeHtmlPipe],
  templateUrl: './onboarding-popup.component.html',
  styleUrls: ['./onboarding-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingPopupComponent implements OnInit {
  private readonly firestore = inject(Firestore);
  private readonly authService = inject(AuthService);
  private readonly sanitizer = inject(DomSanitizer);
  private bootstrapToken = 0;

  readonly ready = signal(false);
  readonly view = signal<OnboardingPopupView | null>(null);
  readonly ctaPressed = signal(false);
  readonly lines = signal<{
    prize_value: string;
    line_1: string;
    line_2: string;
    line_3: string;
  }>({ prize_value: '', line_1: '', line_2: '', line_3: '' });

  private ctaClosing = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: OnboardingPopupDialogData,
    private readonly dialogRef: MatDialogRef<
      OnboardingPopupComponent,
      { reason: OnboardingPopupCloseReason }
    >
  ) {}

  ngOnInit(): void {
    void this.bootstrapWithLanguage();
  }

  pressCta(): void {
    this.ctaPressed.set(true);
  }

  close(reason: OnboardingPopupCloseReason): void {
    if (reason === 'cta') {
      if (this.ctaClosing) {
        return;
      }
      this.ctaClosing = true;
      window.setTimeout(() => this.dialogRef.close({ reason }), 140);
      return;
    }
    this.dialogRef.close({ reason });
  }

  private async bootstrapWithLanguage(): Promise<void> {
    let lang = 'en';
    try {
      const selected = await firstValueFrom(
        this.authService.selectedlanguage$.pipe(take(1))
      );
      if (typeof selected === 'string' && selected.trim()) {
        lang = selected.trim();
      }
    } catch {
      lang = 'en';
    }
    await this.bootstrap(lang);
  }

  private async bootstrap(lang: string): Promise<void> {
    const token = ++this.bootstrapToken;
    try {
      const popupDoc = await this.resolveDoc();
      if (token !== this.bootstrapToken) {
        return;
      }
      if (!popupDoc || popupDoc.active === false) {
        this.close('inactive');
        return;
      }

      const content = pickPopupContent(popupDoc, lang);
      if (token !== this.bootstrapToken) {
        return;
      }
      if (!content) {
        this.close('inactive');
        return;
      }

      const layout = this.resolveLayout(popupDoc.type);
      const images = withPopupInlineImages(popupDoc.images);
      const [prizeValue, line1, line2, line3] = await Promise.all([
        this.renderField(content.prize_value ?? '', images),
        this.renderField(content.line_1 ?? '', images),
        this.renderField(content.line_2 ?? '', images),
        this.renderField(content.line_3 ?? '', images),
      ]);
      if (token !== this.bootstrapToken) {
        return;
      }

      const ctaText = (content.cta_text ?? '').trim();
      const isIntro = layout === 'challenge_intro';

      this.lines.set({
        prize_value: prizeValue,
        line_1: line1,
        line_2: line2,
        line_3: line3,
      });

      const patternUrl =
        layout === 'prize_box_opened' ||
        popupDoc.type === 'prize_unlocked' ||
        this.data?.popupId === 'prize_unlocked'
          ? ''
          : isIntro || popupDoc.type === 'challenge_completed'
            ? ONBOARDING_SPRITE.patternJoystick
            : ONBOARDING_SPRITE.patternKeys;

      this.view.set({
        background: this.trustCss(popupDoc.background || '#9B3DFF'),
        textColor: popupDoc.text_color || '#FFFFFF',
        headerIconBackground: popupDoc.header_icon_background || 'transparent',
        headerIcon: resolveHeaderIcon(popupDoc) ?? '',
        title: content.title ?? '',
        bannerBackground: this.trustCss(
          popupDoc.banner_background ||
            'linear-gradient(90deg, #FF9A3C 0%, #FFE55C 100%)'
        ),
        bannerTextColor: popupDoc.banner_text_color || '#D40000',
        bannerIcon: resolveBannerIcon(popupDoc) ?? '',
        prizeLabel: content.prize_label ?? '',
        panelColor: popupDoc.panel_color || '#6B1FA8',
        rowIcons: resolveRowIcons(popupDoc),
        ctaText,
        ctaBackground: (popupDoc.cta_background || '#FFFFFF').trim(),
        ctaTextColor: (
          popupDoc.cta_text_color || (isIntro ? '#D40000' : '#5B21B6')
        ).trim(),
        ctaShadowColor: (
          popupDoc.cta_shadow_color || (isIntro ? '#FF7A45' : '#7564E3')
        ).trim(),
        showCta: !!ctaText,
        patternCss: this.toSafeCssUrl(patternUrl),
        layout,
      });

      await this.preloadImages(popupDoc);
      if (token !== this.bootstrapToken) {
        return;
      }
      this.ready.set(true);
    } catch (err) {
      this.authService.errorHandlerLogs(
        '[OnboardingPopupComponent] bootstrap failed',
        err
      );
      this.close('unknown');
    }
  }

  private resolveLayout(
    type: string
  ): 'default' | 'prize_box_opened' | 'challenge_intro' {
    const popupId = this.data?.popupId;
    if (popupId === 'prize_box_opened' || type === 'prize_unlocker_credit_added') {
      return 'prize_box_opened';
    }
    if (popupId === 'challenge_intro' || type === 'challenge_intro') {
      return 'challenge_intro';
    }
    return 'default';
  }

  private trustCss(value: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle((value || '').trim());
  }

  private toSafeCssUrl(url: string): string {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      return '';
    }
    const isRelativeAsset = trimmed.startsWith('/assets/');
    const isHttps = /^https:\/\//i.test(trimmed);
    if (!isRelativeAsset && !isHttps) {
      return '';
    }
    const escaped = trimmed.replace(/["'\\]/g, '');
    return `url("${escaped}")`;
  }

  private async resolveDoc(): Promise<GameOnboardingPopupDoc | null> {
    if (this.data?.preloaded) {
      return this.data.preloaded;
    }
    const docId = this.data?.docId;
    if (!docId || typeof docId !== 'string' || docId.includes('/')) {
      return null;
    }
    try {
      const snap = await getDoc(
        doc(this.firestore, `Global_lookup/onboarding/game_new/${docId}`)
      );
      if (!snap.exists()) {
        return null;
      }
      return snap.data() as GameOnboardingPopupDoc;
    } catch {
      return null;
    }
  }

  private async renderField(
    raw: string,
    images: Record<string, string | undefined>
  ): Promise<string> {
    if (!raw) {
      return '';
    }
    let withCurrency = raw;
    if (raw.includes('[currency_beautify:')) {
      try {
        const beautified = await Promise.race([
          this.authService.getDVFromString(raw),
          new Promise<string>((resolve) =>
            setTimeout(() => resolve(fallbackCurrencyBeautifyValue(raw)), 800)
          ),
        ]);
        withCurrency =
          typeof beautified === 'string' && beautified.trim()
            ? beautified
            : fallbackCurrencyBeautifyValue(raw) ||
              stripCurrencyBeautifyTokens(raw);
      } catch {
        withCurrency =
          fallbackCurrencyBeautifyValue(raw) ||
          stripCurrencyBeautifyTokens(raw);
      }
    }
    return renderPopupLine(withCurrency, images);
  }

  private preloadImages(popupDoc: GameOnboardingPopupDoc): Promise<void> {
    const images = popupDoc.images ?? {};
    const urls = [
      resolveHeaderIcon(popupDoc),
      resolveBannerIcon(popupDoc),
      ...resolveRowIcons(popupDoc),
      images.header_icon,
    ].filter((url): url is string => !!url);

    if (!urls.length) {
      return Promise.resolve();
    }

    const loadOne = (src: string) =>
      new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
      });

    return Promise.all(urls.map(loadOne)).then(() => undefined);
  }
}
